import axios, { AxiosRequestConfig } from 'axios';
import config from '@/config/config';
import { useAnonymousUserStore, useUserStore } from '@/store/userStore';
import {
    getMallPathWithProductGroupId,
    getMallUserIdByToken,
    getNotLoginOrder,
    isLogined,
} from '@/services/mallUser.service';
import { logOut } from '@/services/auth.service';
import router, { fetchNStoreAnonymousTokenNId } from '@/router';
import { isMallPath } from '@/utils/checkMallPath';
import { useOrderNumberStore } from '@/store/orderNumberStore';

axios.defaults.withCredentials = true;

export const axiosApi = axios.create({
    headers: {
        'Content-Type': 'application/json',
        TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
});

const ExpiredAccessToken = 205;
const RenewRefreshToken = 201;
// const checkTokenExpiredTime = 5 * 60 * 1000 // 5분
const { api_key, api_secret, common_api, mall_api } = config.api;

const userAccessToken = useUserStore.getState()?.accessToken;
const anonymousUserAccessToken = useAnonymousUserStore.getState()?.accessToken;
const postToken = userAccessToken || anonymousUserAccessToken;
// const { expiresIn: userExpiresIn } = useUserStore.getState()
// const { expiresIn: anonymousExpiresIn } = useAnonymousUserStore.getState()
axiosApi.defaults.headers.common['Authorization'] = `Bearer ${postToken}`;

axiosApi.interceptors.request.use(
    async (req) => {
        if (axiosApi.defaults.headers.common['Authorization']) return req;
        return req;
    },
    (error) => {
        return Promise.reject(error);
    },
);

axiosApi.interceptors.response.use(
    async (res): Promise<any> => {
        if (res?.status !== ExpiredAccessToken) return res;
        console.log('%c isExpired?', 'color:red;font-weight:bold;', res);
        try {
            if (isLogined()) {
                // 로그인 한 유저의 경우 auth/web/refreshToken API를 호출
                try {
                    const refreshTokenResponse = await post(`${common_api}/auth/web/refreshToken`)
                        .then((r) => {
                            console.log(r);
                            // status가 RenewRefreshToken이 아닐 경우 accessToken 갱신에 실패한 상황이므로 로그아웃을 실행한다.
                            if (r.status !== RenewRefreshToken) throw 'token renew fail';
                            else return r;
                        })
                        .catch((error) => {
                            throw 'token renew fail';
                        });
                    // refreshToken API를 호출 후 발급받은 accessToken 및 정보를 전역 상태관리 라이브러리에 세팅한다.
                    useUserStore.setState({
                        accessToken: refreshTokenResponse?.data?.access_token,
                        expiresIn: refreshTokenResponse.data.expires_in,
                    });
                    axiosApi.defaults.headers.common[
                        'Authorization'
                    ] = `Bearer ${refreshTokenResponse?.data?.access_token}`;
                    // 기존 205 response를 받았던 API 요청을 새롭게 발급받은 accessToken을 이용해 재요청한다.
                    try {
                        const reRequest = {
                            ...res.config,
                            headers: { Authorization: `Bearer ${refreshTokenResponse?.data?.access_token}` },
                        };
                        console.log(reRequest);
                        return await axiosApi(reRequest).then((r) => {
                            console.log(r);
                            return r;
                        });
                    } catch (error) {
                        console.log(error);
                        console.log(error);
                        throw error;
                    }
                } catch (error) {
                    if (error === 'token renew fail') {
                        router.navigate(`/logout`);
                    } else throw error;
                }
            } else {
                const { isOrderPage } = useOrderNumberStore.getState();
                try {
                    const isPath = /\/mall\//.test(window.location.pathname);
                    if (isOrderPage && isPath) {
                        const { reset: orderNumberReset } = useOrderNumberStore.getState();
                        orderNumberReset();
                        alert('로그인 세션이 만료되었습니다.');
                        router.navigate(`/mall/${window.location.pathname.split('/')[2]}/not-login-shipping`, {
                            replace: true,
                        });
                    } else {
                        // 비회원의 경우 api_key와 api_secret을 사용해 getToken API와 mall-user-id API를 재호출한다.
                        const { data: tokenData } = await post(`${common_api}/auth/web/getToken`, {
                            api_key,
                            api_secret,
                        }).then((r) => r);
                        useAnonymousUserStore.setState({
                            accessToken: tokenData.access_token,
                            expiresIn: tokenData.expires_in,
                        });
                        const { data: userIdData } = await post(
                            `${mall_api}/main/mall-user-id`,
                            {},
                            { headers: { Authorization: `Bearer ${tokenData.access_token}` } },
                        );
                        useAnonymousUserStore.setState({
                            mallUserId: userIdData.mallUserId,
                        });
                        const reRequest = {
                            ...res.config,
                            headers: { Authorization: `Bearer ${tokenData.access_token}` },
                        };
                        return await axiosApi(reRequest).then((r) => {
                            return r;
                        });
                    }
                } catch (error) {
                    throw error;
                }
            }
        } catch (error) {
            console.log(error);
            throw error;
        }
    },
    async (error) => {
        console.log('api helper Error', error);
        console.log(error?.response.status);
        const path = String(window.location.href).split('/')[4] || '';
        if (error?.response.status === 401) {
            console.log('is 401?', String(window.location.href).split('/'), path);
            fetchNStoreAnonymousTokenNId();
            if (path === 'product-details') {
                const productGroupId = String(window.location.href).split('/')[5] || '';
                const targetPath = await getMallPathWithProductGroupId(productGroupId);
                router.navigate(`/logout?referrer=/mall/${targetPath}`);
            } else {
                router.navigate(`/logout?referrer=${!!path && path !== 'product-details' ? `/mall/${path}` : '/mall'}`);
            }
        }
        return Promise.reject(error?.response);
    },
);

export async function get(url: string, config: AxiosRequestConfig<any> | undefined = {}, verbose = true) {
    return await axiosApi.get(url, config).then((response) => (verbose && response) || response.data);
}
export async function post(url: string, data = {}, config: AxiosRequestConfig<any> | undefined = {}, verbose = true) {
    return await axiosApi
        .post(url, data, { ...config, timeout: 3000 })
        .then((response) => (verbose && response) || response.data);
}

export async function put(url: string, data = {}, config: AxiosRequestConfig<any> | undefined = {}, verbose = true) {
    return await axiosApi
        .put(url, { ...data }, { ...config })
        .then((response) => (verbose && response) || response.data);
}

export async function patch(url: string, data = {}, config: AxiosRequestConfig<any> | undefined = {}, verbose = true) {
    return await axiosApi.patch(url, data, { ...config }).then((response) => (verbose && response) || response.data);
}

export async function del(url: string, config: AxiosRequestConfig<any> | undefined = {}, verbose = true) {
    return await axiosApi.delete(url, config).then((response) => (verbose && response) || response.data);
}
